import {
  StatusBadgeConfigurationResponseSchema,
  type StatusBadgeConfigurationResponse,
} from '@github-ui/dotcom-schema/types/actions/v1/status_badge_configuration'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQuery} from '@github-ui/react-query'

interface StatusBadgeConfigurationOptions {
  owner: string
  repo: string
  workflowFilename: string
  lab?: boolean
  enabled?: boolean
}

/** Builds the repository-scoped endpoint for a workflow's status badge configuration. */
export function statusBadgeConfigurationPath(owner: string, repo: string, workflowFilename: string) {
  const encodedFilename = workflowFilename.split('/').map(encodeURIComponent).join('/')
  return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/actions/workflows/${encodedFilename}/badge`
}

/** Fetches the typed status badge configuration when its dialog opens. */
export function useStatusBadgeConfiguration({
  owner,
  repo,
  workflowFilename,
  lab = false,
  enabled = true,
}: StatusBadgeConfigurationOptions) {
  // Badge configuration is authorization-sensitive and cheap to refresh on each dialog open.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister
  return useQuery<StatusBadgeConfigurationResponse>({
    queryKey: ['status-badge-configuration', owner, repo, workflowFilename, lab],
    enabled: enabled && Boolean(owner && repo && workflowFilename),
    retry: false,
    queryFn: () =>
      queryFnFetch(
        {
          routeId: 'actions-workflows',
          queryName: 'statusBadgeConfiguration',
          queryDeps: {
            pathname: statusBadgeConfigurationPath(owner, repo, workflowFilename),
            searchParams: {lab: lab ? 'true' : null},
          },
        },
        {schema: StatusBadgeConfigurationResponseSchema},
      ),
  })
}
